import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from './images/circet_logo.png';
import { BASEURL } from './constant';
import postApi from './api/postApi';
import { getAuthBodyMessage } from './api/userFacingError';
import { login as authLogin, isAccessTokenValid, logout as authLogout } from './auth';
import MapChart from "./components/charts/MapChart";

const importAll = (r: any) => r.keys().map(r);

const images = importAll((require as any).context('./Event logo', false, /\.(png|jpg)$/));

const testimonials = [
  { type: 'quote', text: 'I wanted to take a moment to thank you for your recent work in monitoring/optimizing our network. This event was a highly anticipated one which T-Mobile prepared for over many months with significant network investment. We needed to have our network perform at the highest level and your team was instrumental in helping us do that on each of the three days.', author: 'RF Manager' },
  { type: 'quote', text: 'I am very impressed with the level of commitment, support, preparation, and execution for this event. FES has done a great job throughout the event. FES was consistent in his update, capturing issues live and taking action to re-adjust accordingly. We truly appreciate this partnership and look forward to future opportunities.', author: 'RF Manager' },
];

const carouselItems = [
  ...images.map((src: string) => ({ type: 'image', src })),
  ...testimonials,
  {type: 'map', title: 'Market Presence'},
];

type AuthView = 'login' | 'otp' | 'forgot' | 'reset';

const formatMmSs = (totalSeconds: number) => {
  const s = Math.max(0, totalSeconds);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [view, setView] = useState<AuthView>('login');
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    if (isAccessTokenValid()) {
      navigate('/home', { replace: true });
    } else {
      authLogout();
    }
  }, [navigate]);

  useEffect(() => {
    if (hovered) return;
    const interval = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % carouselItems.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [hovered]);

  useEffect(() => {
    if ((view !== 'otp' && view !== 'reset') || otpSecondsLeft <= 0) {
      return;
    }
    const timer = window.setTimeout(() => {
      setOtpSecondsLeft((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [view, otpSecondsLeft]);

  const startOtpTimer = (expirySeconds?: number) => {
    const seconds = typeof expirySeconds === 'number' && expirySeconds > 0
      ? expirySeconds
      : 300;
    setOtpSecondsLeft(seconds);
  };

  const handleLogin = async () => {
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setError('');
    setInfo('');
    setBusy(true);
    try {
      const res = await postApi(`${BASEURL}/login`, {
        email: email.toLowerCase(),
        password,
      });
      if (res?.data?.otp_required || res?.data?.status_code === 202) {
        setInfo(res.data.message || 'OTP sent to your email');
        setView('otp');
        setOtp('');
        startOtpTimer(res.data.otp_expiry_seconds);
      } else if (res?.data?.status_code === 200) {
        authLogin(res.data.data, res.data.access_token);
        navigate('/home', { replace: true });
      } else {
        setError(getAuthBodyMessage(res?.data) || 'Login failed');
      }
    } catch (err: any) {
      setError(err.message || 'Request failed — try again');
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otpSecondsLeft <= 0) {
      setError('OTP expired. Please resend a new code.');
      return;
    }
    if (!otp || otp.trim().length < 6) {
      setError('Please enter the 6-digit OTP from your email');
      return;
    }
    setError('');
    setInfo('');
    setBusy(true);
    try {
      const res = await postApi(`${BASEURL}/login`, {
        email: email.toLowerCase(),
        password,
        otp: otp.trim(),
      });
      if (res?.data?.status_code === 200) {
        authLogin(res.data.data, res.data.access_token);
        navigate('/home', { replace: true });
      } else {
        setError(getAuthBodyMessage(res?.data) || 'OTP verification failed');
      }
    } catch (err: any) {
      setError(err.message || 'Request failed — try again');
    } finally {
      setBusy(false);
    }
  };

  const handleResendOtp = async () => {
    setOtp('');
    await handleLogin();
  };

  const handleForgotRequest = async () => {
    if (!email) {
      setError('Please enter your email');
      return;
    }
    setError('');
    setInfo('');
    setBusy(true);
    try {
      const res = await postApi(`${BASEURL}/forgot_password`, {
        email: email.toLowerCase(),
      });
      const code = res?.data?.status_code;
      if (code && code !== 200) {
        setError(getAuthBodyMessage(res.data));
        return;
      }
      setInfo(res?.data?.message || 'If an account exists, a reset code has been sent.');
      setView('reset');
      startOtpTimer(res?.data?.otp_expiry_seconds);
      setResetOtp('');
    } catch (err: any) {
      setError(err.message || 'Request failed — try again');
    } finally {
      setBusy(false);
    }
  };

  const handleResetPassword = async () => {
    if (otpSecondsLeft <= 0) {
      setError('OTP expired. Please request a new reset code.');
      return;
    }
    if (!resetOtp || !newPassword || !confirmPassword) {
      setError('Please fill in OTP and both password fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setError('');
    setInfo('');
    setBusy(true);
    try {
      const res = await postApi(`${BASEURL}/reset_password`, {
        email: email.toLowerCase(),
        otp: resetOtp.trim(),
        new_password: newPassword,
      });
      if (res?.data?.status_code === 200) {
        setInfo('Password reset successfully. You can log in now.');
        setPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setResetOtp('');
        setOtpSecondsLeft(0);
        setView('login');
      } else {
        setError(getAuthBodyMessage(res?.data) || 'Password reset failed');
      }
    } catch (err: any) {
      setError(err.message || 'Request failed — try again');
    } finally {
      setBusy(false);
    }
  };

  const onPrimaryKey = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    if (view === 'login') handleLogin();
    else if (view === 'otp') handleVerifyOtp();
    else if (view === 'forgot') handleForgotRequest();
    else if (view === 'reset') handleResetPassword();
  };

  const otpExpired = otpSecondsLeft <= 0;
  const otpTimerLabel = otpExpired
    ? 'OTP expired'
    : `Code expires in ${formatMmSs(otpSecondsLeft)}`;

  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>
      <div className="d-flex vh-100">
        <div className="col-md-5 d-flex flex-column justify-content-center align-items-center p-5 bg-white">
          <img
            src={logo}
            alt="Circet Logo"
            className="img-fluid"
            style={{ maxHeight: '100px', width: 'auto' }}
          />
          <h2 className="mt-3">Welcome to NMaaS</h2>
          <p className="text-muted">Network Monitoring as a Service Tool</p>

          <div className="w-75 mt-4">
            {view === 'login' && (
              <>
                <div className="form-group mb-3">
                  <label>Email</label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={onPrimaryKey}
                  />
                </div>
                <div className="form-group mb-3">
                  <label>Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="form-control"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={onPrimaryKey}
                      style={{ paddingRight: '45px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="btn position-absolute bg-transparent border-0 p-0"
                      style={{
                        top: '50%',
                        right: '10px',
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        cursor: 'pointer',
                      }}
                      tabIndex={-1}
                    >
                      <i className={`bi ${showPassword ? 'bi-eye' : 'bi-eye-slash'} fs-5 text-secondary`}></i>
                    </button>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="rememberMe" />
                    <label className="form-check-label" htmlFor="rememberMe">
                      Remember me
                    </label>
                  </div>
                  <button
                    type="button"
                    className="btn btn-link p-0 text-primary"
                    onClick={() => {
                      setError('');
                      setInfo('');
                      setView('forgot');
                    }}
                  >
                    Forgot Password?
                  </button>
                </div>

                {error && <div className="alert alert-danger">{error}</div>}
                {info && <div className="alert alert-info">{info}</div>}

                <button
                  className="btn btn-dark w-100"
                  onClick={handleLogin}
                  disabled={busy}
                >
                  {busy ? 'Logging in...' : 'Login'}
                </button>
              </>
            )}

            {view === 'otp' && (
              <>
                <p className="text-muted mb-3">
                  Enter the 6-digit verification code sent to <strong>{email}</strong>
                </p>
                <div
                  className={`alert ${otpExpired ? 'alert-warning' : 'alert-secondary'} py-2 text-center`}
                  role="status"
                >
                  {otpTimerLabel}
                </div>
                <div className="form-group mb-3">
                  <label>OTP</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter OTP"
                    value={otp}
                    maxLength={6}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={onPrimaryKey}
                    autoFocus
                    disabled={otpExpired}
                  />
                </div>
                {error && <div className="alert alert-danger">{error}</div>}
                {info && <div className="alert alert-info">{info}</div>}
                <button
                  className="btn btn-dark w-100 mb-2"
                  onClick={handleVerifyOtp}
                  disabled={busy || otpExpired}
                >
                  {busy ? 'Verifying...' : 'Verify OTP'}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100 mb-2"
                  onClick={handleResendOtp}
                  disabled={busy}
                >
                  Resend OTP
                </button>
                <button
                  type="button"
                  className="btn btn-link w-100"
                  onClick={() => {
                    setView('login');
                    setOtp('');
                    setOtpSecondsLeft(0);
                    setError('');
                    setInfo('');
                  }}
                >
                  Back to login
                </button>
              </>
            )}

            {view === 'forgot' && (
              <>
                <p className="text-muted mb-3">
                  Enter your account email. We will send a reset code if the account exists.
                </p>
                <div className="form-group mb-3">
                  <label>Email</label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={onPrimaryKey}
                  />
                </div>
                {error && <div className="alert alert-danger">{error}</div>}
                {info && <div className="alert alert-info">{info}</div>}
                <button
                  className="btn btn-dark w-100 mb-2"
                  onClick={handleForgotRequest}
                  disabled={busy}
                >
                  {busy ? 'Sending...' : 'Send reset code'}
                </button>
                <button
                  type="button"
                  className="btn btn-link w-100"
                  onClick={() => {
                    setView('login');
                    setError('');
                    setInfo('');
                  }}
                >
                  Back to login
                </button>
              </>
            )}

            {view === 'reset' && (
              <>
                <p className="text-muted mb-3">
                  Enter the code sent to <strong>{email}</strong> and choose a new password.
                </p>
                <div
                  className={`alert ${otpExpired ? 'alert-warning' : 'alert-secondary'} py-2 text-center`}
                  role="status"
                >
                  {otpTimerLabel}
                </div>
                <div className="form-group mb-3">
                  <label>Reset OTP</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter OTP"
                    value={resetOtp}
                    maxLength={6}
                    onChange={(e) => setResetOtp(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={onPrimaryKey}
                    disabled={otpExpired}
                  />
                </div>
                <div className="form-group mb-3">
                  <label>New Password</label>
                  <input
                    type="password"
                    className="form-control"
                    placeholder="At least 8 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    onKeyDown={onPrimaryKey}
                  />
                </div>
                <div className="form-group mb-3">
                  <label>Confirm Password</label>
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onKeyDown={onPrimaryKey}
                  />
                </div>
                {error && <div className="alert alert-danger">{error}</div>}
                {info && <div className="alert alert-info">{info}</div>}
                <button
                  className="btn btn-dark w-100 mb-2"
                  onClick={handleResetPassword}
                  disabled={busy || otpExpired}
                >
                  {busy ? 'Resetting...' : 'Reset password'}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100 mb-2"
                  onClick={handleForgotRequest}
                  disabled={busy}
                >
                  Resend reset code
                </button>
                <button
                  type="button"
                  className="btn btn-link w-100"
                  onClick={() => {
                    setView('login');
                    setOtpSecondsLeft(0);
                    setError('');
                    setInfo('');
                  }}
                >
                  Back to login
                </button>
              </>
            )}
          </div>
        </div>

        <div className="col-md-7 d-none d-md-flex justify-content-center align-items-center"
          style={{
            background: 'linear-gradient(135deg, #ff9966, #ff5e62)',
            borderTopLeftRadius: '2rem',
            borderBottomLeftRadius: '2rem',
            margin: '1rem',
            position: 'relative',
            overflow: 'hidden',
          }}>
          <div className="carousel-wrapper position-relative"
          style={{
            width: '550px',
            height: '500px',
            overflow: 'hidden',
            position: 'relative',
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          >
            {carouselItems.map((item, index) => (
              <div
                key={index}
                className={`carousel-slide ${carouselIndex === index ? 'active' : ''}`}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  padding: '1.5rem',
                  background: '#fff',
                  borderRadius: '1.5rem',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                  transition: 'opacity 0.8s ease-in-out, transform 0.8s ease-in-out',
                  opacity: carouselIndex === index ? 1 : 0.15,
                  zIndex: carouselIndex === index ? 2 : 1,
                  transform: carouselIndex === index ? 'scale(1)' : 'scale(0.9)',
                }}
              >
                {item.type === 'image' ? (
                  <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ textAlign: 'center', paddingBottom: '0.5rem' }}>
                      <h5 className="fw-bold text-center text-dark mb-0">Our Populor Events</h5>
                    </div>
                    <div style={{ flex: 1, borderRadius: '1.5rem', overflow: 'hidden' }}>
                      <img
                        src={item.src}
                        alt="Event Logo"
                        className="img-fluid rounded w-100 h-100 object-fit-contain"
                      />
                    </div>
                  </div>
                ) : item.type === 'quote' ? (
                  <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ textAlign: 'center', paddingBottom: '0.5rem' }}>
                      <h5 className="fw-bold text-center text-dark mb-0">Our Customer Say</h5>
                    </div>
                    <div style={{ flex: 1, borderRadius: '1.5rem', overflow: 'hidden' }}>
                      <p className="fs-5 text-dark">"{item.text}"</p>
                      <p className="text-muted small">- {item.author}</p>
                    </div>
                  </div>
                ) : item.type === 'map' ? (
                  <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ textAlign: 'center', paddingBottom: '0.5rem' }}>
                      <h5 className="fw-bold mb-0 text-dark">Our Presence</h5>
                    </div>
                    <div style={{ flex: 1, borderRadius: '1.5rem', overflow: 'hidden' }}>
                      <MapChart />
                    </div>
                  </div>
                ): null}
              </div>
            ))}
            <div
              className="position-absolute bottom-0 start-50 translate-middle-x d-flex gap-2 pb-3"
              style={{ zIndex: 3 }}
            >
              {carouselItems.map((_, idx) => (
                <div
                  key={idx}
                  onClick={() => setCarouselIndex(idx)}
                  style={{
                    width: carouselIndex === idx ? '14px' : '10px',
                    height: carouselIndex === idx ? '14px' : '10px',
                    borderRadius: '50%',
                    backgroundColor: carouselIndex === idx ? '#ff5e62' : '#ccc',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
